import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { WIPCampaignMetrics } from "../../../store/apis/wipApi";
import { EnrichedRow, enrichRow } from "../buyMedia/derive";
import Filters from "../buyMedia/Filters";
import { buildFacets, matchesWipFilters } from "../buyMedia/filterFields";
import { STATUS_COLOR, STATUS_LABEL } from "../buyMedia/columns";
import WipLineCharts from "./WipLineCharts";

type TypeFilter = "ALL" | "PAID_MEDIA_MANAGED" | "BUY_MEDIA_DIRECT_AND_OWN";

// La subcampaña elegida, para que siga abierta al volver. Comodidad de quien mira:
// si el navegador no la guarda, se abre la primera de la lista.
const SELECTED_KEY = "beapolo_wip_charts_selected";

const readSelected = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
};

// Los filtros, igual que en las tablas del WIP: se guardan al cambiarlos y se
// recuperan al volver. Si el navegador no los guarda, se empieza sin filtros.
const FILTERS_KEY = "beapolo_wip_charts_filters";

interface ChartsFilters {
  typeFilter: TypeFilter;
  statusFilter: string[];
  textFilter: string;
  fieldFilters: Record<string, string[]>;
}

const DEFAULT_FILTERS: ChartsFilters = {
  typeFilter: "ALL",
  statusFilter: [],
  textFilter: "",
  fieldFilters: {},
};

const TYPE_FILTERS: TypeFilter[] = [
  "ALL",
  "PAID_MEDIA_MANAGED",
  "BUY_MEDIA_DIRECT_AND_OWN",
];

const readFilters = (): ChartsFilters => {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const saved = JSON.parse(raw) as Partial<ChartsFilters>;
    const fieldFilters: Record<string, string[]> = {};
    if (saved.fieldFilters && typeof saved.fieldFilters === "object") {
      for (const [field, values] of Object.entries(saved.fieldFilters)) {
        if (Array.isArray(values)) fieldFilters[field] = values.map(String);
      }
    }
    return {
      typeFilter: TYPE_FILTERS.includes(saved.typeFilter as TypeFilter)
        ? (saved.typeFilter as TypeFilter)
        : "ALL",
      statusFilter: Array.isArray(saved.statusFilter)
        ? saved.statusFilter.map(String)
        : [],
      textFilter: typeof saved.textFilter === "string" ? saved.textFilter : "",
      fieldFilters,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
};

interface WipChartsViewProps {
  /** Todas las líneas del mes, Paid Media y Buy Media juntas. */
  rows: WIPCampaignMetrics[];
}

/** Lista filtrable de subcampañas; al elegir una se ven sus gráficas. */
const WipChartsView: React.FC<WipChartsViewProps> = ({ rows }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [filters, setFilters] = useState<ChartsFilters>(readFilters);
  const { typeFilter, statusFilter, textFilter, fieldFilters } = filters;
  const setTypeFilter = (v: TypeFilter) =>
    setFilters((prev) => ({ ...prev, typeFilter: v }));
  const setStatusFilter = (v: string[]) =>
    setFilters((prev) => ({ ...prev, statusFilter: v }));
  const setTextFilter = (v: string) =>
    setFilters((prev) => ({ ...prev, textFilter: v }));

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    } catch {
      // ignore quota errors
    }
  }, [filters]);
  const [selectedId, setSelectedId] = useState<string | null>(readSelected);

  const enrichedRows: EnrichedRow[] = useMemo(
    () => rows.map(enrichRow),
    [rows]
  );
  const typedRows = useMemo(
    () =>
      typeFilter === "ALL"
        ? enrichedRows
        : enrichedRows.filter((r) => r.briefingType === typeFilter),
    [enrichedRows, typeFilter]
  );
  const facets = useMemo(
    () => buildFacets(typedRows, fieldFilters),
    [typedRows, fieldFilters]
  );
  const filteredRows = useMemo(
    () =>
      typedRows
        .filter((r) =>
          matchesWipFilters(r, { statusFilter, textFilter, fieldFilters })
        )
        .sort(
          (a, b) =>
            (a.companyName ?? "").localeCompare(b.companyName ?? "") ||
            a.associatedCampaignName.localeCompare(b.associatedCampaignName)
        ),
    [typedRows, statusFilter, textFilter, fieldFilters]
  );

  const counts = useMemo(
    () => ({
      ALL: enrichedRows.length,
      PAID_MEDIA_MANAGED: enrichedRows.filter(
        (r) => r.briefingType === "PAID_MEDIA_MANAGED"
      ).length,
      BUY_MEDIA_DIRECT_AND_OWN: enrichedRows.filter(
        (r) => r.briefingType === "BUY_MEDIA_DIRECT_AND_OWN"
      ).length,
    }),
    [enrichedRows]
  );

  const selected =
    filteredRows.find((r) => r.associatedCampaignId === selectedId) ?? null;

  // Si los filtros dejan fuera la elegida, se abre la primera que queda.
  useEffect(() => {
    if (!selected && filteredRows.length > 0) {
      setSelectedId(filteredRows[0].associatedCampaignId);
    }
  }, [selected, filteredRows]);

  const select = (id: string) => {
    setSelectedId(id);
    try {
      localStorage.setItem(SELECTED_KEY, id);
    } catch {
      // ignore quota errors
    }
  };

  const clearFilters = () =>
    setFilters((prev) => ({
      ...prev,
      statusFilter: [],
      textFilter: "",
      fieldFilters: {},
    }));

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={typeFilter}
          onChange={(_e, v: TypeFilter | null) => v && setTypeFilter(v)}
          sx={{ alignSelf: "flex-start", flexWrap: "wrap" }}
        >
          <ToggleButton value="ALL" sx={{ textTransform: "none" }}>
            {t("wip.charts.typeAll")} · {counts.ALL}
          </ToggleButton>
          <ToggleButton
            value="PAID_MEDIA_MANAGED"
            sx={{ textTransform: "none" }}
          >
            {t("wip.tabPaidMedia")} · {counts.PAID_MEDIA_MANAGED}
          </ToggleButton>
          <ToggleButton
            value="BUY_MEDIA_DIRECT_AND_OWN"
            sx={{ textTransform: "none" }}
          >
            {t("wip.tabBuyMedia")} · {counts.BUY_MEDIA_DIRECT_AND_OWN}
          </ToggleButton>
        </ToggleButtonGroup>

        <Filters
          statusFilter={statusFilter}
          textFilter={textFilter}
          fieldFilters={fieldFilters}
          facets={facets}
          onStatusChange={setStatusFilter}
          onTextChange={setTextFilter}
          onFieldFilterChange={(field, values) =>
            setFilters((prev) => {
              const next = { ...prev.fieldFilters };
              if (values.length === 0) delete next[field];
              else next[field] = values;
              return { ...prev, fieldFilters: next };
            })
          }
          onClear={clearFilters}
        />
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "340px minmax(0, 1fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[200]}`,
            overflow: "hidden",
            position: { md: "sticky" },
            top: { md: 16 },
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 2, pt: 1.5 }}
          >
            {t("wip.subcampaignCount", { count: filteredRows.length })}
          </Typography>
          <List
            dense
            sx={{
              maxHeight: { xs: 280, md: "70vh" },
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {filteredRows.map((r) => (
              <ListItemButton
                key={r.associatedCampaignId}
                selected={
                  r.associatedCampaignId === selected?.associatedCampaignId
                }
                onClick={() => select(r.associatedCampaignId)}
                sx={{ alignItems: "flex-start", gap: 1 }}
              >
                <Box
                  aria-hidden
                  sx={{
                    mt: 0.9,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: STATUS_COLOR[r.status],
                  }}
                />
                <ListItemText
                  primary={r.associatedCampaignName}
                  secondary={`${r.companyName || "—"} · ${r.platform} · ${STATUS_LABEL[r.status]}`}
                  // Los nombres suelen ir sin espacios (VERTEX_TRAVEL_SUB_4_…):
                  // "anywhere" los parte donde haga falta en vez de desbordar.
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                  slotProps={{
                    primary: { sx: { fontSize: 13, fontWeight: 600 } },
                    secondary: { sx: { fontSize: 12 } },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
          {filteredRows.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, pb: 2 }}
            >
              {t("wip.charts.noLines")}
            </Typography>
          )}
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          {selected ? (
            <WipLineCharts row={selected} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {t("wip.charts.selectLine")}
            </Typography>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

export default WipChartsView;
